import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import api from '../lib/api';
import { TotalizadorCards } from '@/components/financeiro/TotalizadorCards';
import { FinanceiroSelectDropdown } from '@/components/financeiro/FinanceiroSelectDropdown';
import { FINANCEIRO_MESES } from '@/components/financeiro/financeiroPeriodo';
import {
  FINANCEIRO_FILTER_FONT as FILTER_FONT,
  FINANCEIRO_FILTER_INPUT as FILTER_FIELD,
  FINANCEIRO_FILTER_LABEL as FILTER_LABEL,
  FINANCEIRO_FILTER_RADIUS as FILTER_RADIUS,
  FINANCEIRO_FILTER_SHADOW as FILTER_SHADOW,
} from '@/components/financeiro/financeiroFilterFieldStyles';
import type { ResumoMensalDto, LancamentoDto } from '@prometeus/contracts';

export const Route = createFileRoute('/w/$workspaceId/financeiro/resumo')({
  component: ResumoMensalPage,
});

const MONTH_OPTIONS = FINANCEIRO_MESES.map((label, i) => ({
  value: String(i + 1),
  label,
}));

function fmt(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function getRecebidoParcial(tags: string[] | null): number {
  const tag = tags?.find((t) => t.startsWith('RECEBIDO_PARCIAL='));
  if (!tag) return 0;
  return Number(tag.split('=')[1]) || 0;
}

function getSaldoEmAberto(l: LancamentoDto): number {
  if (l.status !== 'PENDENTE') return l.valor;
  return Math.max(0, l.valor - getRecebidoParcial(l.tags));
}

function getSituacaoBadge(l: LancamentoDto) {
  const isParcial = l.tags?.includes('PARCIAL');
  if (l.status === 'EFETUADO') {
    return (
      <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
        Efetuado
      </span>
    );
  }
  if (isParcial) {
    return (
      <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
        Parcial
      </span>
    );
  }
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const venc = l.dataVencimento ? new Date(l.dataVencimento) : null;
  if (venc && venc < hoje) {
    return (
      <span className="inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-700 dark:bg-rose-500/20 dark:text-rose-300">
        Vencido
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:bg-sky-500/20 dark:text-sky-300">
      Previsto
    </span>
  );
}

function ResumoMensalPage() {
  const { workspaceId } = Route.useParams();
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());

  const { data, isLoading } = useQuery<ResumoMensalDto>({
    queryKey: ['financeiro', workspaceId, 'resumo', mes, ano],
    queryFn: async () => {
      const res = await api.get(`/financeiro/resumo?mes=${mes}&ano=${ano}`);
      return res.data.data ?? res.data;
    },
  });

  return (
    <div className="space-y-5 rounded-2xl border-0 bg-[#FFFFFF] p-5 shadow-[0_0_15px_rgba(0,0,0,0.05)] outline-none ring-0 dark:bg-card dark:shadow-[0_0_15px_rgba(0,0,0,0.05)] sm:p-6 md:p-6">
      <div className="min-w-0">
        <h2 className="font-sans text-base font-normal tracking-normal text-[#06141B] dark:text-foreground">
          Resumo mensal
        </h2>
        <p className="mt-1 max-w-2xl font-sans text-xs font-normal text-[#9BA8AB] dark:text-muted-foreground">
          Visão consolidada de entradas, saídas e saldo do período.
        </p>
      </div>

      <div
        className={`border-0 bg-[#F4F5F5] p-4 ${FILTER_RADIUS} ${FILTER_SHADOW} outline-none ring-0 dark:bg-muted/40 dark:shadow-[0_0_15px_rgba(0,0,0,0.05)] sm:p-5`}
      >
        <div className={`flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end ${FILTER_FONT}`}>
          <div className="min-w-[200px] flex-1 space-y-1.5 sm:max-w-xs">
            <p className={FILTER_LABEL}>Mês</p>
            <FinanceiroSelectDropdown
              value={String(mes)}
              onChange={(v) => setMes(Number(v))}
              options={MONTH_OPTIONS}
            />
          </div>
          <div className="w-full min-w-[120px] max-w-[140px] space-y-1.5">
            <p className={FILTER_LABEL}>Ano</p>
            <input
              type="number"
              value={ano}
              min={2000}
              max={2100}
              onChange={(e) => setAno(Number(e.target.value))}
              className={`${FILTER_FIELD} h-10`}
            />
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-lg border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
          Carregando resumo do período...
        </div>
      )}

      {data && (
        <>
          <TotalizadorCards resumo={data} />

          <div className="overflow-hidden rounded-[10px] bg-[#FFFFFF] shadow-[0_0_15px_rgba(0,0,0,0.05)] dark:bg-card dark:shadow-[0_0_15px_rgba(0,0,0,0.05)]">
            <table className="w-full text-[12px] text-[#06141B] dark:text-foreground" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <thead>
                <tr
                  className="border-b border-black/[0.06] dark:border-white/10"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  <th className="px-3 py-3 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Situação
                  </th>
                  <th className="px-3 py-3 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Tipo
                  </th>
                  <th className="px-3 py-3 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Descrição
                  </th>
                  <th className="px-3 py-3 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Categoria
                  </th>
                  <th className="px-3 py-3 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Vencimento
                  </th>
                  <th className="px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Total
                  </th>
                  <th className="px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Em Aberto
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.lancamentos.map((l: LancamentoDto) => {
                  const isParcial = l.tags?.includes('PARCIAL');
                  const saldo = getSaldoEmAberto(l);
                  return (
                  <tr
                    key={l.id}
                    className="border-t border-black/[0.06] transition-colors hover:bg-[#F8FAFC]/80 dark:border-white/10 dark:hover:bg-muted/20"
                  >
                    <td className="px-3 py-2.5">{getSituacaoBadge(l)}</td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          l.tipo === 'ENTRADA'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                            : 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300'
                        }`}
                      >
                        {l.tipo === 'ENTRADA' ? 'Entrada' : 'Saída'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">{l.descricao}</td>
                    <td className="px-3 py-2.5 text-[#4A5C6A] dark:text-muted-foreground">{l.categoria?.nome ?? '—'}</td>
                    <td className="px-3 py-2.5 text-[#4A5C6A] dark:text-muted-foreground">
                      {l.dataVencimento ? new Date(l.dataVencimento).toLocaleDateString('pt-BR') : '—'}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-right tabular-nums ${
                        isParcial ? 'text-[#4A5C6A] line-through dark:text-muted-foreground' : `font-semibold ${l.tipo === 'ENTRADA' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`
                      }`}
                    >
                      {l.tipo === 'SAIDA' ? '−' : ''}
                      {fmt(l.valor)}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-right font-semibold tabular-nums ${
                        l.status === 'EFETUADO'
                          ? 'text-[#4A5C6A] dark:text-muted-foreground'
                          : l.tipo === 'ENTRADA'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-500 dark:text-red-400'
                      }`}
                    >
                      {l.status === 'EFETUADO'
                        ? '—'
                        : `${l.tipo === 'SAIDA' ? '−' : ''}${fmt(saldo)}`}
                    </td>
                  </tr>
                  );
                })}
                {data.lancamentos.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                      Nenhum lançamento no período
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
